import { FunctionComponent } from "react";
import { CartesianGrid, XAxis, YAxis, Label, Bar, Line, LabelList } from "recharts";
import { CustomizedAxisTick, RoundedCustomizedBarLabel, CustomTooltip } from "../ChartUtil";
import { ComposedChart, ResponsiveContainer, Tooltip as ChartTooltip } from "recharts";
import { ContentType } from "recharts/types/component/Label";
import { colorsLight, Body1 } from "@coherehealth/common";

export interface WorkingTimeChartData {
  name: string;
  workingTime: number;
  idleTime: number;
  qph: number;
}
interface TimeWorkedProps {
  data: WorkingTimeChartData[];
  hourly?: boolean;
}

const TimeWorkedChart = ({ data, hourly }: TimeWorkedProps) => {
  return (
    <ResponsiveContainer width={"100%"} height={"95%"}>
      <ComposedChart
        data={data}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        barGap={0}
      >
        <CartesianGrid stroke="rgba(0, 0, 0, 0.08)" vertical={false} />
        <XAxis
          dataKey="name"
          axisLine={{ stroke: "rgba(0, 0, 0, 0.08)" }}
          tickLine={false}
          tick={(props) => <CustomizedAxisTick hourly={hourly} {...props} />}
          minTickGap={-250}
        />
        <YAxis
          axisLine={{ stroke: "rgba(0, 0, 0, 0.08)" }}
          tickLine={{ stroke: "rgba(0, 0, 0, 0.08)" }}
          stroke={colorsLight.font.light}
          type="number"
          tickCount={5}
          interval={0}
          tick={{
            fontFamily: "Figtree, sans-serif",
            fontWeight: 600,
            fontSize: "14px",
            letterSpacing: "0px",
          }}
          domain={["auto", (dataMax: number) => +(dataMax * 1.33).toFixed(1)]}
          yAxisId="left"
        >
          <Label
            style={{
              textAnchor: "middle",
              fill: colorsLight.font.secondary,
              fontFamily: "Figtree, sans-serif",
              fontWeight: 600,
              fontSize: "15px",
              letterSpacing: "0px",
            }}
            angle={270}
            value={"Hours worked"}
            position="insideLeft"
          />
        </YAxis>
        <YAxis
          axisLine={{ stroke: "rgba(0, 0, 0, 0.08)" }}
          tickLine={{ stroke: "rgba(0, 0, 0, 0.08)" }}
          stroke={colorsLight.font.light}
          type="number"
          tickCount={5}
          interval={0}
          tick={{
            fontFamily: "Figtree, sans-serif",
            fontWeight: 600,
            fontSize: "14px",
            letterSpacing: "0px",
          }}
          domain={["auto", (dataMax: number) => +(dataMax * 1.33).toFixed(1)]}
          yAxisId="right"
          orientation="right"
        >
          <Label
            style={{
              textAnchor: "middle",
              fill: colorsLight.font.secondary,
              fontFamily: "Figtree, sans-serif",
              fontWeight: 600,
              fontSize: "15px",
              letterSpacing: "0px",
            }}
            angle={270}
            value={"QPH"}
            position="insideRight"
          />
        </YAxis>
        <ChartTooltip content={<CustomTooltip Content={TooltipContent} />} wrapperStyle={{ outline: "none" }} />
        <Bar
          isAnimationActive={false}
          dataKey="workingTime"
          yAxisId="left"
          fill="#02718B"
          stackId="a"
          label={RoundedCustomizedBarLabel}
        />
        <Bar
          isAnimationActive={false}
          dataKey="idleTime"
          yAxisId="left"
          fill="#B1B4B9"
          stackId="a"
          label={RoundedCustomizedBarLabel}
        />
        <Line
          isAnimationActive={false}
          dataKey="qph"
          yAxisId="right"
          type="linear"
          stroke="#EA1D63"
          strokeWidth={2.5}
          dot={{ fill: "#EA1D63", strokeWidth: 0 }}
        >
          <LabelList content={customizedLineLabel} />
        </Line>
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const customizedLineLabel: ContentType = (props) => {
  const { x, y, value } = props;

  return (
    <text x={x} y={y} dy={-5} fill="#EA1D63" fontSize={15} fontFamily="Figtree" fontWeight={600} textAnchor="middle">
      {value}
    </text>
  );
};

const TooltipContent: FunctionComponent<React.PropsWithChildren<{ payload: any }>> = ({ payload }) => {
  return (
    <div style={{ margin: "5px" }}>
      <Body1 style={{ marginBottom: "20px" }}>Total: {+(payload[0].value + payload[1].value).toFixed(1)} hrs</Body1>
      <Body1>
        Working time: <span style={{ color: payload[0].fill }}>{payload[0].value} hrs</span>
      </Body1>
      <Body1>
        Idle time: <span style={{ color: payload[1].fill }}>{payload[1].value} hrs</span>
      </Body1>
    </div>
  );
};

export default TimeWorkedChart;
